import { useEffect, useMemo } from 'react';

import { GMetrixTableCellProps, GMetrixTableColumnProps } from 'components/GMetrixTable/components';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { DefaultLayout, PageWithHeader } from 'components/layout';
import { helpContent } from 'pages/orders/my_inventory/help';
import { FormattedMessage, useIntl } from 'react-intl';
import { Order, OrderApi } from 'apis/OrderApi';
import { PartnerTypes } from 'apis/PartnerApi';
import { Alert, Card } from 'react-bootstrap';
import { GMetrixTable } from 'components';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

export const MyInventory = () => {

    const { highestRole, selectedPartner } = useApp();
    const isTestingCenter = selectedPartner.type === PartnerTypes.TestingCenter;

    const [state, setState] = useSuperState({
        data: [] as Order[],
        error: null as string,
        loading: true
    });
    const { error, loading } = state;

    const { formatMessage } = useIntl();

    const clearError = () => setState({ error: null });

    const fetchInventory = async () => {

        try {

            setState({
                error: '',
                loading: true
            });

            const data = await OrderApi.GetMyInventory(selectedPartner.id, isTestingCenter);

            setState({
                data,
                loading: false
            });

        } catch (e: any) {

            setState({
                error: e?.message ?? 'Failed to load inventory.',
                loading: false
            });

        }

    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {

        fetchInventory();

    }, [selectedPartner.id]);

    const TypeCell = (cellProps: GMetrixTableCellProps<Order>) => {

        const value = cellProps.dataItem?.UsesByType ?? '';
        const lines = value.split('\n').filter(Boolean);

        return (
            <td>
                <ul className={'list-unstyled m-0'}>
                    {lines.map((line, key) => (
                        <li key={key}>
                            {line}
                        </li>

                    ))}
                </ul>
            </td>
        );

    };

    const columns = useMemo((): GMetrixTableColumnProps<Order>[] => [
        { field: 'Name', title: formatMessage({ id: 'license_name.8773f362', defaultMessage: 'License Name' }) },
        { cell: TypeCell, field: 'UsesByType', hidden: !isTestingCenter, title: formatMessage({ id: 'type.b65913c1', defaultMessage: 'Type', description: 'Formulario' }) },
        { field: 'NumAvailable', hidden: isTestingCenter, title: formatMessage({ id: 'available.d321561a', defaultMessage: 'Available' }) },
        { field: 'DateCreate', title: formatMessage({ id: 'ordered.61d11bd3', defaultMessage: 'Ordered' }) },
        { field: 'DateExpire', hidden: !isTestingCenter, title: formatMessage({ id: 'expires.84d49534', defaultMessage: 'Expires' }) },
        { field: 'PurchaseOrder', title: formatMessage({ id: 'purchase_order.17844493', defaultMessage: 'Purchase Order' }) },
        { field: 'Comment', hidden: highestRole.id !== 1, title: formatMessage({ id: 'comment.252dd7a3', defaultMessage: 'Comment', description: 'Formulario' }) }
    ], [highestRole.id, isTestingCenter]);

    return (
        <DefaultLayout pageTitle={formatMessage({ id: 'my_inventory.21568e1d', defaultMessage: 'My Inventory' })}>
            <PageWithHeader
                headerConfig={{
                    cta: highestRole.id !== 1 ? {
                        label: formatMessage({ id: 'order_more_inventory.454ebc12', defaultMessage: 'Order More Inventory' }),
                        to: '/orders/place?OrderFor=1'
                    } : undefined,
                    helpContent: helpContent,
                    title: (
                        <span className="d-flex align-items-center gap-2">
                            {<FormattedMessage defaultMessage="My Inventory" id="my_inventory.21568e1d" />}
                        </span>
                    )
                }}
            >
                <div className="my-inventory d-flex flex-column gap-3">

                    <Card body >
                        <Alert dismissible onClose={clearError} show={!!error} variant={'danger'}>
                            {error}
                        </Alert>

                        {loading ?
                            (
                                <div className="d-flex align-items-center justify-content-center p-5">
                                    <GMetrixLoader />
                                </div>
                            )
                            :
                            (
                                <GMetrixTable
                                    allowExport
                                    columnData={columns as any}
                                    defaultItemsPerPage={50}
                                    excelExportFileName={formatMessage({ id: 'myinventory.26f355b0', defaultMessage: 'MyInventory' })}
                                    rowData={state.data}
                                    tableName={formatMessage({ id: 'myinventory.26f355b0', defaultMessage: 'MyInventory' })}
                                />
                            )
                        }
                    </Card>
                </div>
            </PageWithHeader>
        </DefaultLayout>
    );

};
